// #2121-spec-kits
import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { Badge, UncontrolledTooltip } from 'reactstrap';
import { translate } from 'react-translate';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { actionSetEditOrder, actionDeleteOrder } from 'store/actions/practiceOrdersActions';
import { actionGetKitTypes } from 'store/actions/kitsActions';
import SweetAlert from 'react-bootstrap-sweetalert';
import { actionSweetAlert } from 'store/actions/commonActions';
import { toast } from 'react-toastify';
import moment from 'moment';

const STATUS_TONE = {
  o_st_pending:           'secondary',
  o_st_in_preparation:    'info',
  o_st_readytodeliver:    'primary',
  o_st_inpostmail:        'warning',
  o_st_receivedbypractice:'success',
  o_st_cancel:            'danger',
};

const TablePracticeOrders = (props) => {
  const { data, page, callback, t } = props;
  const dispatch = useDispatch();
  const history  = useHistory();
  const kitTypes = useSelector((s) => s.kitsReducer.types);

  useEffect(() => {
    if (!Object.keys(kitTypes).length) dispatch(actionGetKitTypes());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const editOrder = (e, order) => {
    e.preventDefault();
    dispatch(actionSetEditOrder({ ...order }));
    history.push(`/admin/practice-orders/form/${order._id}`);
  };

  const copyId = (e, val) => {
    e.preventDefault();
    e.stopPropagation();
    navigator.clipboard.writeText(val);
    toast.success(t('id-copied') || 'Copied');
  };

  const deleteOrder = (e, order) => {
    e.preventDefault();
    dispatch(
      actionSweetAlert(
        <SweetAlert
          warning
          title={t('order-cancel-title')}
          onConfirm={() => {
            dispatch(actionDeleteOrder(order._id));
            if (data.length === 1 && page > 0) callback(page - 1);
            dispatch(actionSweetAlert(null));
          }}
          onCancel={() => dispatch(actionSweetAlert(null))}
          confirmBtnBsStyle="danger"
          cancelBtnBsStyle="info"
          confirmBtnText={t('order-cancel-confirm-btn')}
          cancelBtnText={t('order-cancel-deny-btn')}
          showCancel
        >
          {t('order-cancel-description')}
        </SweetAlert>
      )
    );
  };

  return (
    <div className="kit-table-wrap">
      <table className="kit-table">
        <thead>
          <tr>
            <th style={{ width: '110px' }}>ID</th>
            <th>{t('practice')}</th>
            <th style={{ width: '180px' }}>{t('status')}</th>
            <th style={{ width: '160px' }}>{t('created-at')}</th>
            <th>{t('kits')}</th>
            <th style={{ width: '110px', textAlign: 'right' }}>{t('actions')}</th>
          </tr>
        </thead>
        <tbody>
          {data.map((p, idx) => {
            const tone = STATUS_TONE[p.status && p.status.status_id] || 'secondary';
            const orderedBy = p.ordered_by || {};
            const owner = orderedBy.practice_owner || {};
            const isCancelled = p.status && p.status.status_id === 'o_st_cancel';
            return (
              <tr key={p._id || idx} className={`kit-row tone-${tone}`}>
                <td>
                  <div className="kit-id-cell">
                    <span className="kit-id-code" style={{ fontSize: '0.78rem' }}>
                      {String(p._id).slice(-6).toUpperCase()}
                    </span>
                    <button
                      type="button"
                      className="kit-id-copy"
                      onClick={(e) => copyId(e, p._id)}
                      id={`po-copy-${idx}`}
                      aria-label="Copy"
                    >
                      <i className="now-ui-icons files_single-copy-04" />
                    </button>
                    <UncontrolledTooltip target={`po-copy-${idx}`}>{p._id}</UncontrolledTooltip>
                  </div>
                </td>

                <td>
                  <div className="kit-practice-cell">
                    <div className="kit-practice-name">{orderedBy.practice_name || '—'}</div>
                    {(owner.firstName || owner.lastName) && (
                      <div className="kit-practice-owner">
                        {owner.firstName} {owner.lastName}
                      </div>
                    )}
                  </div>
                </td>

                <td>
                  <Badge color={tone} pill className="kit-status-badge">
                    {t(p.status && p.status.status_id)}
                  </Badge>
                </td>

                <td>
                  <time className="kit-status-time">
                    {p.created_at ? moment(p.created_at).format('DD-MM-YYYY HH:mm:ss') : '—'}
                  </time>
                </td>

                <td>
                  <div className="order-kits-row">
                    {(p.requested_kits || []).map((rk, i) => {
                      if (!rk.requested_quantity || rk.requested_quantity <= 0) return null;
                      const fulfilled = rk.prepared_quantity >= rk.requested_quantity;
                      const cls = isCancelled
                        ? 'order-kit-pill order-kit-cancelled'
                        : `order-kit-pill ${fulfilled ? 'fulfilled' : 'pending'}`;
                      return (
                        <span key={`rk-${idx}-${i}`} className={cls}>
                          <span className="order-kit-type">{rk.kit_type_id}</span>
                          <span className="order-kit-counts">
                            {rk.prepared_quantity}/{rk.requested_quantity}
                          </span>
                        </span>
                      );
                    })}
                  </div>
                </td>

                <td className="kit-actions-cell">
                  {!isCancelled && (
                    <div className="kit-actions">
                      <button
                        type="button"
                        className="kit-action-btn"
                        onClick={(e) => editOrder(e, p)}
                        id={`po-edit-${idx}`}
                      >
                        <i className="fa fa-edit" />
                      </button>
                      <UncontrolledTooltip target={`po-edit-${idx}`}>{t('edit-btn') || 'Edit'}</UncontrolledTooltip>

                      <button
                        type="button"
                        className="kit-action-btn kit-action-danger"
                        onClick={(e) => deleteOrder(e, p)}
                        id={`po-del-${idx}`}
                      >
                        <i className="fa fa-trash" />
                      </button>
                      <UncontrolledTooltip target={`po-del-${idx}`}>{t('delete-btn') || 'Delete'}</UncontrolledTooltip>
                    </div>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

TablePracticeOrders.defaultProps = { data: [], loading: true };
TablePracticeOrders.propTypes    = { data: PropTypes.array, loading: PropTypes.bool };

export default translate('practice-orders')(TablePracticeOrders);
